import {process, State} from "@progress/kendo-data-query"
import {
    Grid,
    GridCellProps,
    GridColumn,
    GridColumnMenuFilter,
    GridColumnMenuProps,
    GridDataStateChangeEvent,
    GridDetailRowProps,
} from "@progress/kendo-react-grid"
import {Checkbox} from "@progress/kendo-react-inputs"
import {useEffect, useState} from "react"
import {useDispatch} from "react-redux"
import {
    fetchCustomPricingTestPackages,
    setCustomPriceTestPackageModel,
} from "../../containers/CustomPrices/customPricesSlice"
import {RootState} from "../../rootReducer"
import {useAppSelector} from "../../store"
import {hasPermission} from "../Shared/permissionChecker"
import {Tooltip} from "@progress/kendo-react-tooltip"
import { useInternationalization } from "@progress/kendo-react-intl"

const DATA_ITEM_KEY = "dealerId"

const ManageCustomPriceTestPackages = (): JSX.Element => {
    const dispatch = useDispatch()
    const selectedRegionId = useAppSelector((state: RootState) => state.containers.customPrices.selectedRegionId)
    const testPackages = useAppSelector((state: RootState) => state.containers.customPrices.customPriceTestPackages)

    const customPricingId = useAppSelector((state: RootState) => state.containers.customPrices.selectedCustomPricingId)
    const assignedPermissions = useAppSelector((state: RootState) => state.application.permissions)
    const verifyPermission = (checkPermissions: string[]) => hasPermission(checkPermissions, assignedPermissions)
    const initialState: State = {skip: 0, take: 100}

    const [dataState, setDataState] = useState(initialState)
    const [testPackage, setTestPackage] = useState<any | null>(testPackages)

    useEffect(() => {
        if (customPricingId >= 0 && selectedRegionId > 0)
           { dispatch(fetchCustomPricingTestPackages(selectedRegionId, customPricingId)) }
    }, [dispatch, selectedRegionId, customPricingId])

    useEffect(() => {
        setDataState(initialState)
        setTestPackage(testPackages)
    }, [testPackages])

    const toggleCustomPriceTestPackageReport = (dealerId: number | null, isExpanded: boolean) => {
        if (testPackage) {
            const testPackageReport = testPackage.map((x: any) => {
                if (x.dealerID === dealerId) {
                    return {...x, expanded: isExpanded}
                }
                return x
            })
            setTestPackage(testPackageReport)
        }
    }

    //column expanding logic
    const ExpandSelectionCell = (props: GridCellProps) => {
        const dealerTestPackages: any = props.dataItem

        return (
            <td className="k-hierarchy-cell">
                {props.dataItem.customPricingTestPackages.length > 0 && (
                    <a
                        onClick={() =>
                            toggleCustomPriceTestPackageReport(
                                dealerTestPackages.dealerID,
                                !dealerTestPackages.expanded,
                            )
                        }
                        className={`k-icon k-i-caret-alt-${dealerTestPackages.expanded ? "down" : "right"}`}
                    ></a>
                )}
            </td>
        )
    }

    const CommonColumnMenu = (props: GridColumnMenuProps) => (
        <GridColumnMenuFilter {...props} expanded={true} hideSecondFilter={true} />
    )
    return (
        testPackages && (
            <Grid
                style={{height: `calc(100vh -  ${verifyPermission(["CustomPricing.Edit"]) ? "27.75rem" : "24.8rem"})`}}
                dataItemKey={DATA_ITEM_KEY}
                selectable={{
                    enabled: false,
                    drag: false,
                    cell: false,
                    mode: "multiple",
                }}
                data={process(testPackage, dataState)}
                {...dataState}
                expandField="expanded"
                onDataStateChange={(e: GridDataStateChangeEvent) => setDataState(e.dataState)}
                detail={TestPackageGrid}
            >
                <GridColumn
                    field="expanded"
                    cell={ExpandSelectionCell}
                    width="40px"
                    headerCell={() => <></>}
                    columnMenu={CommonColumnMenu}
                />
                <GridColumn field="dealerName" title="DealerName" width={"auto"} />
            </Grid>
        )
    )
}

export default ManageCustomPriceTestPackages

export const TestPackageGrid = (gridDetailRowProps: GridDetailRowProps): JSX.Element => {
    const [dataState, setDataState] = useState<any | null>(gridDetailRowProps.dataItem.customPricingTestPackages)
    const dispatch = useDispatch()
    const intl = useInternationalization()

    const checkBoxColumn = (props: any) => {
        return (
            <Tooltip openDelay={100} position="bottom" anchorElement={"pointer"}>
                {
                    <td
                        colSpan={1}
                        role="gridcell"
                        aria-colindex={1}
                        title={`${props.dataItem.active ? "" : "Package is deactivated"}`}
                        style={{cursor: `${props.dataItem.active ? "pointer" : "not-allowed"}`}}
                    >
                        <Checkbox
                            style={{opacity: 1}}
                            onClick={e => {
                                const updatedTestPackages = dataState.map((d: any) => {
                                    if (d.testPackageID === props.dataItem.testPackageID) {
                                        return {
                                            ...d,
                                            selected: !props.dataItem.selected,
                                        }
                                    } else {
                                        return d
                                    }
                                })
                                setDataState(updatedTestPackages)
                                dispatch(
                                    setCustomPriceTestPackageModel({
                                        dealerID: props.dataItem.dealerID,
                                        updatedTestPackages,
                                    }),
                                )
                            }}
                            checked={props.dataItem.selected}
                            disabled={!props.dataItem.active}
                        />
                    </td>
                }
            </Tooltip>
        )
    }

    const FeeColumnCell = (props: GridCellProps) => {
        return (
            <td className="text-end pe-4">
                {props.dataItem != null
                    ? intl.formatNumber(props.dataItem[`${props.field}`], {style: "currency", minimumFractionDigits: 2})
                    : ""}
            </td>
        )
    }
    return (
        <Grid
            data={dataState}
            dataItemKey="testPackageID"
            selectedField="selected"
            selectable={{
                enabled: false,
                drag: false,
                cell: false,
                mode: "multiple",
            }}
            expandField="expanded"
        >
            <GridColumn width={80} cell={props => checkBoxColumn(props)}></GridColumn>

            <GridColumn field="testPackageName" title="TestPackageName" width={200} />
            <GridColumn field="testPackageDescription" title="TestPackageDescription" />
            <GridColumn field="laneFee" title="LaneFee" width={100} cell={FeeColumnCell} />
            <GridColumn field="testFee" title="TestFee" width={100} cell={FeeColumnCell} />
        </Grid>
    )
}
